import { Tag, Award, Zap, Heart, Store, MapPin, Clock } from 'lucide-react';

interface Perk {
  id: string;
  title: string;
  merchant: string;
  category: string;
  discount: number;
  originalPrice: number | null;
  discountedPrice: number | null;
  currency: string;
  expiryDate: string;
  location: string;
  description: string;
  isFlashDeal: boolean;
  isEmployerSponsored: boolean;
  imageUrl: string | null;
  imageAlt: string;
}

interface PerkCardProps {
  perk: Perk;
  onPerkClick: () => void;
  onMerchantClick: () => void;
  isWishlisted: boolean;
  onToggleWishlist: () => void;
}

const PerkCard = ({ perk, onPerkClick, onMerchantClick, isWishlisted, onToggleWishlist }: PerkCardProps) => {
  const daysUntilExpiry = Math.ceil(
    (new Date(perk.expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer group">
      {/* Image Section */}
      <div className="relative h-48 bg-gradient-to-br from-primary/10 to-primary/5 overflow-hidden">
        {perk.imageUrl ? (
          <img
            src={perk.imageUrl}
            alt={perk.imageAlt || `${perk.title} promotional image`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Tag className="w-12 h-12 text-primary/20" />
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {perk.isEmployerSponsored && (
            <span className="inline-flex items-center px-2 py-1 bg-blue-600 text-white text-xs font-semibold rounded">
              <Award className="w-3 h-3 mr-1" />
              Sponsored
            </span>
          )}
          {perk.isFlashDeal && (
            <span className="inline-flex items-center px-2 py-1 bg-red-600 text-white text-xs font-semibold rounded animate-pulse">
              <Zap className="w-3 h-3 mr-1" />
              Flash Deal
            </span>
          )}
        </div>

        {/* Wishlist Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleWishlist();
          }}
          className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-sm hover:shadow-md transition-shadow duration-200"
        >
          <Heart
            className={`w-4 h-4 ${isWishlisted ? 'text-red-500 fill-current' : 'text-gray-600'}`}
          />
        </button>

        {/* Discount Badge */}
        <div className="absolute bottom-3 right-3 bg-green-600 text-white px-3 py-1 rounded-full font-bold text-sm">
          {perk.discount}% OFF
        </div>
      </div>

      {/* Content Section */}
      <div className="p-4" onClick={onPerkClick}>
        {/* Merchant */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onMerchantClick();
          }}
          className="flex items-center text-sm text-gray-600 hover:text-primary mb-2 transition-colors duration-150"
        >
          <Store className="w-3.5 h-3.5 mr-1" />
          {perk.merchant}
        </button>

        {/* Title */}
        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-primary transition-colors duration-150">
          {perk.title}
        </h3>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
          {perk.description}
        </p>

        {/* Price */}
        {perk.originalPrice && (
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-sm text-gray-500 line-through">
              {perk.currency} {perk.originalPrice}
            </span>
            <span className="text-lg font-bold text-green-600">
              {perk.currency} {perk.discountedPrice}
            </span>
          </div>
        )}

        {/* Footer Info */}
        <div className="flex items-center justify-between pt-3 border-t border-border">
          <div className="flex items-center text-xs text-gray-500">
            <MapPin className="w-3 h-3 mr-1" />
            {perk.location}
          </div>
          <div className={`flex items-center text-xs ${daysUntilExpiry <= 7 ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
            <Clock className="w-3 h-3 mr-1" />
            {daysUntilExpiry} days left
          </div>
        </div>

        {/* Action Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPerkClick();
          }}
          className="w-full mt-3 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors duration-200"
        >
          View Details
        </button>
      </div>
    </div>
  );
};

export default PerkCard;
